import React from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  Paper,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import { AddCircleOutline, Group } from "@mui/icons-material";
import type { Club, ClubMember, InvoiceByPeriodItem, OneOffSession, TrainingGroup, UpcomingTrainingDate } from "./types";
import PricingBadge from "./PricingBadge";
import InvoicesByPeriodSection from "./InvoicesByPeriodSection";
import ClubMembersSection from "./ClubMembersSection";
import TrainingGroupSection from "./TrainingGroupSection";
import OneOffSessionSection from "./OneOffSessionSection";
import SandboxModeAlert from "./SandboxModeAlert";
import VenueHireSection from "./VenueHireSection";
import { featureFlags } from "../../utils/featureFlags";

interface ClubCardProps {
  club: Club;
  expandedTrainingGroupKey: string | null;
  setExpandedTrainingGroupKey: (key: string | null) => void;
  scrollToExpandedGroupRef: React.RefObject<HTMLDivElement>;
  onAddClubMember: (clubId: number) => void;
  onRemoveClubMember: (clubId: number, member: ClubMember) => void;
  onCreateGroup: (clubId: number) => void;
  onCreateOneOffSession?: (clubId: number) => void;
  onAddGroupMember: (club: Club, group: TrainingGroup) => void;
  onAddOneOffParticipant?: (club: Club, session: OneOffSession) => void;
  onIssueInvoice: (club: Club, group: TrainingGroup) => void;
  onIssueOneOffInvoices?: (session: OneOffSession) => void;
  onCancelOneOffSession?: (session: OneOffSession) => void;
  onSessionBookingSettings?: (session: OneOffSession) => void;
  onOneOffSessionRefresh?: () => Promise<void> | void;
  onOneOffSessionError?: (message: string) => void;
  expandedOneOffSessionKey?: string | null;
  setExpandedOneOffSessionKey?: (key: string | null) => void;
  onRenameGroup: (club: Club, group: TrainingGroup) => void;
  onArchiveGroup: (group: TrainingGroup) => void;
  onSetGroupDefaultFee: (club: Club, group: TrainingGroup) => void;
  onRemoveGroupMember: (group: TrainingGroup, member: ClubMember) => void;
  onEditNotes: (group: TrainingGroup, date: UpcomingTrainingDate) => void;
  expandedVenueHireClubId: number | null;
  onVenueHireExpandChange: (clubId: number | null) => void;
  onVenueHireSettings: (club: Club) => void;
  onAddVenueAvailability: (club: Club) => void;
  onBlockVenueTime: (club: Club) => void;
  onVenueHireRefresh: () => Promise<void> | void;
  onVenueHireError: (message: string) => void;
  venueHireRefreshKey: number;
  onVoidInvoice?: (invoice: InvoiceByPeriodItem) => void;
}

const ClubCard: React.FC<ClubCardProps> = ({
  club,
  expandedTrainingGroupKey,
  setExpandedTrainingGroupKey,
  scrollToExpandedGroupRef,
  onAddClubMember,
  onRemoveClubMember,
  onCreateGroup,
  onCreateOneOffSession,
  onAddGroupMember,
  onAddOneOffParticipant,
  onIssueInvoice,
  onIssueOneOffInvoices,
  onCancelOneOffSession,
  onSessionBookingSettings,
  onOneOffSessionRefresh,
  onOneOffSessionError,
  expandedOneOffSessionKey,
  setExpandedOneOffSessionKey,
  onRenameGroup,
  onArchiveGroup,
  onSetGroupDefaultFee,
  onRemoveGroupMember,
  onEditNotes,
  expandedVenueHireClubId,
  onVenueHireExpandChange,
  onVenueHireSettings,
  onAddVenueAvailability,
  onBlockVenueTime,
  onVenueHireRefresh,
  onVenueHireError,
  venueHireRefreshKey,
  onVoidInvoice,
}) => {
  const theme = useTheme();
  const memberCount = club.members?.length ?? 0;
  const groupCount = club.training_groups?.length ?? 0;
  const oneOffSessionCount = club.one_off_sessions?.length ?? 0;

  return (
    <Card
      elevation={3}
      sx={{
        borderRadius: 2,
        border: `1px solid ${theme.palette.divider}`,
      }}
    >
      <CardContent sx={{ p: { xs: 1.5, sm: 3 } }}>
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            justifyContent: "space-between",
            alignItems: { xs: "stretch", md: "center" },
            gap: { xs: 2, md: 0 },
            mb: { xs: 2, sm: 3 },
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: { xs: 1.5, sm: 2 },
              minWidth: 0,
            }}
          >
            <Group
              sx={{
                fontSize: { xs: 32, sm: 40 },
                color: theme.palette.primary.main,
                flexShrink: 0,
              }}
            />
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 600,
                  color: theme.palette.primary.main,
                  fontSize: { xs: "1.15rem", sm: "1.5rem" },
                  wordBreak: "break-word",
                }}
              >
                {club.name}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {memberCount} member{memberCount !== 1 ? "s" : ""} •{" "}
                {groupCount} training group
                {groupCount !== 1 ? "s" : ""}
                {featureFlags.oneOffSessions &&
                  ` • ${oneOffSessionCount} one-off session${oneOffSessionCount !== 1 ? "s" : ""}`}
              </Typography>
              <Stack direction="row" spacing={1} sx={{ mt: 0.5 }} flexWrap="wrap" useFlexGap>
                {club.is_sandbox && (
                  <Chip
                    label="Sandbox"
                    size="small"
                    color="warning"
                    variant="outlined"
                  />
                )}
                <PricingBadge memberCount={memberCount} />
              </Stack>
            </Box>
          </Box>
          <Stack
            direction="row"
            spacing={1}
            useFlexGap
            flexWrap="wrap"
            sx={{
              flexShrink: 0,
              "& > .MuiButton-root": {
                flex: { xs: 1, sm: "0 0 auto" },
                minWidth: 0,
              },
            }}
          >
            <Button
              variant="outlined"
              startIcon={<AddCircleOutline />}
              size="small"
              onClick={() => onAddClubMember(club.id)}
            >
              Add Member
            </Button>
            <Button
              variant="outlined"
              startIcon={<AddCircleOutline />}
              size="small"
              onClick={() => onCreateGroup(club.id)}
            >
              Create Group
            </Button>
            {featureFlags.oneOffSessions && onCreateOneOffSession && (
              <Button
                variant="outlined"
                startIcon={<AddCircleOutline />}
                size="small"
                onClick={() => onCreateOneOffSession(club.id)}
              >
                Add One-off Session
              </Button>
            )}
          </Stack>
        </Box>

        <Divider sx={{ mb: { xs: 2, sm: 3 } }} />

        {club.is_sandbox && <SandboxModeAlert />}

        {featureFlags.venueHire && (
          <VenueHireSection
            club={club}
            expanded={expandedVenueHireClubId === club.id}
            onExpandChange={(isExpanded) =>
              onVenueHireExpandChange(isExpanded ? club.id : null)
            }
            onOpenSettings={onVenueHireSettings}
            onAddAvailability={onAddVenueAvailability}
            onBlockTime={onBlockVenueTime}
            onRefreshClub={onVenueHireRefresh}
            onError={onVenueHireError}
            refreshKey={venueHireRefreshKey}
          />
        )}

        {club.invoices_by_period && club.invoices_by_period.length > 0 && (
          <InvoicesByPeriodSection
            periods={club.invoices_by_period}
            onVoidInvoice={onVoidInvoice}
          />
        )}

        <ClubMembersSection
          members={club.members}
          onRemove={(member) => onRemoveClubMember(club.id, member)}
        />

        {club.training_groups?.map((group, groupIndex) => {
          const groupKey = `${club.id}-${group.group_name}`;
          const isExpandedGroup = expandedTrainingGroupKey === groupKey;
          return (
            <TrainingGroupSection
              key={groupIndex}
              club={club}
              group={group}
              expanded={isExpandedGroup}
              onExpandChange={(isExpanded) =>
                setExpandedTrainingGroupKey(isExpanded ? groupKey : null)
              }
              onAddMember={onAddGroupMember}
              onIssueInvoice={onIssueInvoice}
              onRename={onRenameGroup}
              onArchive={onArchiveGroup}
              onSetDefaultFee={onSetGroupDefaultFee}
              onRemoveMember={onRemoveGroupMember}
              onEditNotes={onEditNotes}
              containerRef={isExpandedGroup ? scrollToExpandedGroupRef : undefined}
            />
          );
        })}

        {featureFlags.oneOffSessions &&
          club.one_off_sessions?.map((session) => {
            const sessionKey = `${club.id}-one-off-${session.id}`;
            const isExpandedSession = expandedOneOffSessionKey === sessionKey;
            return (
              <OneOffSessionSection
                key={session.id}
                club={club}
                session={session}
                expanded={isExpandedSession}
                onExpandChange={(isExpanded) =>
                  setExpandedOneOffSessionKey?.(
                    isExpanded ? sessionKey : null,
                  )
                }
                onAddParticipant={(c, s) => onAddOneOffParticipant?.(c, s)}
                onIssueInvoices={(s) => onIssueOneOffInvoices?.(s)}
                onCancel={(s) => onCancelOneOffSession?.(s)}
                onBookingSettings={(s) => onSessionBookingSettings?.(s)}
                onRefresh={() => onOneOffSessionRefresh?.() ?? Promise.resolve()}
                onError={(msg) => onOneOffSessionError?.(msg)}
              />
            );
          })}

        {groupCount === 0 && !featureFlags.oneOffSessions && (
          <Paper
            variant="outlined"
            sx={{
              p: 2,
              mt: 2,
              textAlign: "center",
              bgcolor: theme.palette.grey[50],
            }}
          >
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              No training groups yet. Create one to get started.
            </Typography>
            <Button
              variant="outlined"
              startIcon={<AddCircleOutline />}
              size="small"
              onClick={() => onCreateGroup(club.id)}
            >
              Create Group
            </Button>
          </Paper>
        )}
      </CardContent>
    </Card>
  );
};

export default ClubCard;
